import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { api, type FlatFileRecipeRow } from '../lib/api.js';
import { RecipeList, recipeLineageCount } from '../components/RecipeList.js';

// Per-EHR import-readiness (design §10): "can I re-import this EHR untouched?"
function readiness(recipes: FlatFileRecipeRow[]): { label: string; color: string } {
  const active = recipes.filter((r) => r.status === 'accepted');
  if (active.length === 0) return { label: 'no active recipe', color: 'var(--text-dimmed)' };
  const verdicts = active.map((r) => (r.health as { verdict?: string } | null)?.verdict).filter(Boolean);
  if (verdicts.includes('FAIL')) return { label: 'needs re-scope', color: 'var(--accent-red)' };
  if (verdicts.includes('PASS')) return { label: 'ready', color: 'var(--accent-green)' };
  return { label: 'ready · unchecked', color: 'var(--accent-yellow)' };
}

// Top-level cross-EHR recipe library — the discoverable home for reusable flat-file
// mappings (so the feature isn't buried under each EHR's detail tab). Grouped by EHR.
export function RecipesPage() {
  usePageTitle('Recipes');
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['all-flat-file-recipes'],
    queryFn: () => api.listAllFlatFileRecipes(),
    refetchInterval: 30_000,
  });
  const recipes = data?.recipes ?? [];

  const byEhr = new Map<string, { ehrId: string; name: string; recipes: FlatFileRecipeRow[] }>();
  for (const r of recipes) {
    const g = byEhr.get(r.ehrId) ?? byEhr.set(r.ehrId, { ehrId: r.ehrId, name: r.ehrDisplayName ?? r.ehrId, recipes: [] }).get(r.ehrId)!;
    g.recipes.push(r);
  }
  const groups = [...byEhr.values()].sort((a, b) => a.name.localeCompare(b.name));
  const active = recipes.filter((r) => r.status === 'accepted').length;
  const onChanged = () => void qc.invalidateQueries({ queryKey: ['all-flat-file-recipes'] });

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-14 items-center gap-3 border-b border-border px-6 shrink-0">
        <span className="text-sm font-semibold text-text-primary">Recipes</span>
        <span className="text-[11px] text-text-dimmed">reusable flat-file mappings across all EHRs — active on authoring, gated by the health check</span>
        {recipes.length > 0 && (
          <span className="ml-auto text-[11px] text-text-dimmed">
            {groups.length} EHR{groups.length !== 1 ? 's' : ''} · {recipeLineageCount(recipes)} shapes · {active} active
          </span>
        )}
      </header>

      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="text-sm text-text-dimmed">Loading…</div>
        ) : groups.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center max-w-[700px]" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
            <div className="text-sm text-text-secondary">No flat-file recipes yet.</div>
            <div className="text-[11px] text-text-dimmed mt-1">
              Drop files for a source EHR in a session and author a mapping — it’s saved here and reused automatically on the next matching drop.
            </div>
          </div>
        ) : (
          <div className="space-y-7 max-w-[1000px]">
            {groups.map((g) => (
              <div key={g.ehrId}>
                <div className="flex items-center gap-2 mb-2">
                  <Link to="/ehrs/$id" params={{ id: g.ehrId }} className="text-sm font-semibold hover:underline" style={{ color: 'var(--accent-blue)' }}>
                    {g.name}
                  </Link>
                  <span className="text-[11px] text-text-dimmed">
                    {recipeLineageCount(g.recipes)} shape{recipeLineageCount(g.recipes) !== 1 ? 's' : ''}
                  </span>
                  {(() => {
                    const r = readiness(g.recipes);
                    return (
                      <span className="text-[10px] font-semibold rounded-full px-2 py-0.5" style={{ color: r.color, background: `color-mix(in srgb, ${r.color} 12%, transparent)`, border: `1px solid ${r.color}44` }}>
                        {r.label}
                      </span>
                    );
                  })()}
                </div>
                <RecipeList recipes={g.recipes} onChanged={onChanged} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
